/**
 * Protected Route Component
 * დაცული როუტი - ავტორიზაციის შემოწმება
 */

import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: string[];
  requireAllRoles?: boolean;
  redirectTo?: string;
  UnauthorizedComponent?: React.ReactNode;
  useKeycloakLogin?: boolean;
}

/**
 * ProtectedRoute - Wraps routes that require authentication
 *
 * @example
 * // Basic protection
 * <ProtectedRoute>
 *   <Dashboard />
 * </ProtectedRoute>
 *
 * @example
 * // With role requirement
 * <ProtectedRoute roles={['admin']}>
 *   <AdminPanel />
 * </ProtectedRoute>
 *
 * @example
 * // Require all roles
 * <ProtectedRoute roles={['admin', 'manager']} requireAllRoles>
 *   <SuperAdminPanel />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  roles,
  requireAllRoles = false,
  redirectTo,
  UnauthorizedComponent,
  useKeycloakLogin = true,
}: ProtectedRouteProps) {
  // wasCallback comes from AuthProvider - tracked before keycloak clears URL params
  const { isAuthenticated, isLoading, user, login, hasRole, hasAnyRole, wasCallback } = useAuth();
  const loginTriggered = useRef(false);

  // Trigger Keycloak login if not authenticated
  // Don't trigger if we came from a callback - that would cause infinite loop
  useEffect(() => {
    if (!isLoading && !isAuthenticated && useKeycloakLogin && !loginTriggered.current && !wasCallback) {
      loginTriggered.current = true;
      login();
    }
  }, [isLoading, isAuthenticated, useKeycloakLogin, login, wasCallback]);

  // Still loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Waiting for login redirect (only if not from callback)
  if (!isAuthenticated && useKeycloakLogin && !wasCallback && !loginTriggered.current) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  // Not authenticated - redirect to login page or landing
  // If we came from a callback that failed, redirect to landing (/) not login
  if (!isAuthenticated) {
    const fallbackPath = wasCallback ? '/' : (redirectTo || '/');
    return <Navigate to={fallbackPath} replace />;
  }

  // Check roles if specified
  if (roles && roles.length > 0) {
    const hasRequiredRoles = requireAllRoles
      ? roles.every((role) => hasRole(role))
      : hasAnyRole(roles);

    if (!hasRequiredRoles) {
      if (UnauthorizedComponent) {
        return <>{UnauthorizedComponent}</>;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen text-center px-4">
          <div className="text-6xl mb-4">403</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-gray-500">
            Required roles: {roles.join(', ')}
          </p>
          <p className="text-sm text-gray-500">
            Your roles: {user?.roles.join(', ') || 'None'}
          </p>
        </div>
      );
    }
  }

  return <>{children}</>;
}

/**
 * Public Route - Redirects authenticated users
 */
interface PublicRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

export function PublicRoute({
  children,
  redirectTo = '/dashboard',
}: PublicRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
